import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAgentCatalog, getMyAgents, createAgent, fundAgent, withdrawAgent,
  pauseAgent, resumeAgent, getAgentDiary, getDiarioExterno, getAgentLibrary, apiError,
  getPropuestasBots, aprobarPropuestaBot, descartarPropuestaBot,
} from '../lib/agents';
import ConectaTuAgente from '../components/ConectaTuAgente';
import PendientesAgente from '../components/PendientesAgente';
import MandatoAgente from '../components/MandatoAgente';
import {
  useSesionesConectadas, fetchActividad, filtrarDiarioPorGrupo, canalDe, permisosDe,
  fetchLlaves, reactivarLlave,
} from '../components/AgentesConectados';

/**
 * Mis Agentes (Modo Agente, F3 DEMO) — el cliente conoce los bots pre-armados,
 * crea el suyo CON NOMBRE, lo fondea con plata de PRACTICA (ficticia), y lee
 * lo que hizo: primera persona, en espanol. Nada aqui mueve dinero real.
 *
 * Dos vistas en la pestaña agentes:
 *   EQUIPO (default) — una columna, personas.
 *   AGENTE ABIERTO — su historia es la pantalla.
 */

const EVENTO_STYLE = {
  compra: { dot: 'bg-blue-500', label: 'Compra' },
  venta_tp: { dot: 'bg-green-500', label: 'Venta con ganancia' },
  venta_sl: { dot: 'bg-red-500', label: 'Venta por stop' },
  venta_trailing: { dot: 'bg-green-500', label: 'Ganancia asegurada' },
  skip: { dot: 'bg-gray-400', label: 'Decidió no entrar' },
  fondeo: { dot: 'bg-emerald-500', label: 'Fondeo' },
  retiro: { dot: 'bg-gray-500', label: 'Retiro' },
  creacion: { dot: 'bg-indigo-500', label: 'Nacimiento' },
  pausa: { dot: 'bg-gray-500', label: 'Pausa' },
  reanudacion: { dot: 'bg-blue-500', label: 'De vuelta al trabajo' },
  freno: { dot: 'bg-red-600', label: 'Freno de seguridad' },
  cooldown: { dot: 'bg-amber-500', label: 'Pausa preventiva' },
  miguel: { dot: 'bg-[#C9A961]', label: 'Miguel · para entender' },
  propuesta: { dot: 'bg-[#2A59C6]', label: 'Pidió tu permiso' },
  propuesta_vencida: { dot: 'bg-gray-400', label: 'Propuesta vencida' },
};

const FICHA_TESIS = {
  constante: { horizonte: 'Años', tesis: 'el tiempo en el mercado gana al timing' },
  paciente: { horizonte: 'Semanas', tesis: 'solo montado en tendencia' },
  cazador: { horizonte: 'Días', tesis: 'rebote en rango, no perseguir caídas con noticias feas' },
  guardian: { horizonte: 'Años', tesis: 'proteger lo ya ganado' },
};

const FICHA_MOOD = {
  constante: '#0a2540',
  paciente: '#2A59C6',
  cazador: '#C2410C',
  guardian: '#166534',
};

const FICHA_VOZ = {
  constante: {
    yo: 'Cada semana reparto entre el mundo: Estados Unidos, tecnología, dividendos, emergentes, bonos y oro. No vendo.',
    noHace: 'Nunca vende.',
  },
  paciente: {
    yo: 'Solo compro cuando la tendencia ya está a favor. Si se da vuelta, me salgo.',
    noHace: 'No adivina pisos.',
  },
  cazador: {
    yo: 'Compro el rebote en el piso del rango. No persigo caídas con noticias feas.',
    noHace: 'No caza caídas libres.',
  },
  guardian: {
    yo: 'Armo una canasta defensiva y la cuido. Estoy para proteger lo ganado.',
    noHace: 'No persigue subidas.',
  },
};

const HISTORIA_VACIA = 'Todavía no ha hecho nada.';

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (d) => (d ? new Date(d).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function historiaDesdeDiario(e) {
  return {
    id: e.id || e._id,
    createdAt: e.createdAt,
    texto: String(e.texto || e.motivo || '').trim(),
    bloqueado: Boolean(e.bloqueado),
  };
}

function historiaDesdeGateway(a) {
  const bloqueado = a.ok === false || (a.statusCode && a.statusCode >= 400);
  const base = a.label || a.tool || '';
  const sufijo = bloqueado
    ? (a.statusCode === 501 ? ' — no le dejé mover dinero.' : ' — no pasó.')
    : '';
  return {
    id: a.id,
    createdAt: a.ts,
    texto: `${base}${sufijo}`,
    bloqueado,
  };
}

function invertidoDe(a) {
  return (a.posiciones || []).reduce((s, p) => s + Number(p.valor || 0), 0);
}

export default function Agentes() {
  const location = useLocation();
  const [catalogo, setCatalogo] = useState([]);
  const [proximamente, setProximamente] = useState([]);
  const [fondeoInicial, setFondeoInicial] = useState(1000);
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null); // { ok, text }

  // creacion
  const [creando, setCreando] = useState(null); // preset key elegido
  const [nombre, setNombre] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorCard, setErrorCard] = useState(null); // { preset, text } — error en ESA carta

  // selectedId: id de bot pre-armado, o `ia:${label}` de un grupo de sesiones
  const [selectedId, setSelectedId] = useState(null);
  const [diario, setDiario] = useState([]);
  const [citas, setCitas] = useState({}); // agentId → última línea (no bloquea)
  const [comoDe, setComoDe] = useState(null); // preset con "¿Cómo decide?" abierto
  const [feedIA, setFeedIA] = useState([]);
  const [notaFeedIA, setNotaFeedIA] = useState(null);

  const [mostrarCodigo, setMostrarCodigo] = useState(false);
  const [mostrarProximamente, setMostrarProximamente] = useState(false);

  const [tab, setTab] = useState('agentes');

  const [biblioteca, setBiblioteca] = useState([]);
  const [docAbierto, setDocAbierto] = useState(null);
  useEffect(() => {
    getAgentLibrary().then((d) => setBiblioteca(d.docs || [])).catch(() => {});
  }, []);

  const { grupos, loading: loadingIA, revocar, cargar: cargarSesiones } = useSesionesConectadas();

  const [propuestas, setPropuestas] = useState([]);
  const [propBusy, setPropBusy] = useState(null);
  const [propMsg, setPropMsg] = useState(null);
  const cargarPropuestas = useCallback(() => {
    getPropuestasBots().then((d) => setPropuestas(d || [])).catch(() => {});
  }, []);
  useEffect(() => {
    cargarPropuestas();
    const t = setInterval(cargarPropuestas, 20000);
    return () => clearInterval(t);
  }, [cargarPropuestas]);

  const onAprobarPropuesta = async (pr) => {
    setPropBusy(pr.id); setPropMsg(null);
    try {
      await aprobarPropuestaBot(pr.id);
      setPropMsg({ id: pr.id, ok: true, texto: `Listo: ${pr.nombreBot} compró ${pr.symbol} por $${pr.monto}. Está en su diario.` });
      setPropuestas((prev) => prev.filter((x) => x.id !== pr.id));
      cargar();
    } catch (e) {
      setPropMsg({ id: pr.id, ok: false, texto: apiError(e, 'No se pudo aprobar. Intenta de nuevo.') });
    } finally { setPropBusy(null); }
  };
  const onDescartarPropuesta = async (pr) => {
    setPropBusy(pr.id); setPropMsg(null);
    try {
      await descartarPropuestaBot(pr.id);
      setPropuestas((prev) => prev.filter((x) => x.id !== pr.id));
    } catch (e) {
      setPropMsg({ id: pr.id, ok: false, texto: apiError(e, 'No se pudo descartar. Intenta de nuevo.') });
    } finally { setPropBusy(null); }
  };

  const cargar = useCallback(async () => {
    try {
      const [cat, mios] = await Promise.all([getAgentCatalog(), getMyAgents().catch(() => [])]);
      setCatalogo(cat.catalogo || []);
      setProximamente(cat.proximamente || []);
      setFondeoInicial(Number(cat.protections?.fondeoInicialDemo) || 1000);
      setAgentes(mios || []);
    } catch {
      setMsg({ ok: false, text: 'No pudimos cargar los agentes. Intenta de nuevo.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const aviso = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); };

  const onCrear = async (preset) => {
    if (!nombre.trim()) {
      const text = 'Ponle un nombre a tu agente';
      setErrorCard({ preset, text });
      return aviso(false, text);
    }
    setEnviando(true);
    setErrorCard(null);
    try {
      await createAgent({ nombre: nombre.trim(), preset });
      aviso(true, `Nació ${nombre.trim()} con ${fmtUsd(fondeoInicial)} de práctica.`);
      setCreando(null); setNombre('');
      setErrorCard(null);
      setSelectedId(null);
      cargar();
    } catch (e) {
      const text = apiError(e, 'No pudimos crear tu agente');
      setErrorCard({ preset, text });
      aviso(false, text);
    } finally { setEnviando(false); }
  };

  const recargarHistoria = async (agente) => {
    try {
      const d = await getAgentDiary(agente.id);
      setDiario(d.eventos || []);
    } catch {
      setDiario([]);
    }
  };

  const onMonto = async (agente, accion) => {
    const verbo = accion === 'fondear' ? 'darle' : 'retirarle';
    const raw = window.prompt(`¿Cuánto quieres ${verbo} a ${agente.nombre}? (USD de práctica)`);
    if (raw == null) return;
    const monto = Number(String(raw).replace(/[^0-9.]/g, ''));
    if (!monto || monto <= 0) return aviso(false, 'Monto inválido');
    try {
      if (accion === 'fondear') await fundAgent(agente.id, monto);
      else await withdrawAgent(agente.id, monto);
      aviso(true, accion === 'fondear' ? `${agente.nombre} recibió ${fmtUsd(monto)} de práctica` : `Retiraste ${fmtUsd(monto)}`);
      await cargar();
      if (selectedId === agente.id) recargarHistoria(agente);
    } catch (e) { aviso(false, apiError(e, 'No se pudo completar')); }
  };

  const onToggle = async (agente) => {
    try {
      if (agente.estado === 'activo') await pauseAgent(agente.id);
      else await resumeAgent(agente.id);
      await cargar();
      if (selectedId === agente.id) recargarHistoria(agente);
    } catch (e) { aviso(false, apiError(e, 'No se pudo cambiar el estado')); }
  };

  const onDiario = async (agente) => {
    setSelectedId(agente.id);
    setDiario([]);
    setFeedIA([]);
    setNotaFeedIA(null);
    try {
      const d = await getAgentDiary(agente.id);
      setDiario(d.eventos || []);
    } catch { aviso(false, 'No pudimos cargar lo que hizo'); }
  };

  const abrirIA = async (grupo) => {
    setSelectedId(grupo.key);
    setDiario([]);
    setFeedIA([]);
    setNotaFeedIA(null);
    try {
      const d = await getDiarioExterno();
      const { eventos, nota } = filtrarDiarioPorGrupo(d?.eventos || [], grupo, grupos.length);
      let feed = eventos.map(historiaDesdeDiario).filter((h) => h.texto);
      let notaFinal = nota;
      // Diario canonico VACIO no es "no ha hecho nada": la IA pudo trabajar sin
      // escribir su diario todavia (el Vigia barriendo señales, p.ej.). En ese caso
      // mostramos su actividad tecnica del gateway, con la nota honesta.
      if (!feed.length) {
        try {
          const entries = await fetchActividad(grupo.sessions.map((s) => s.agentId));
          const tecnica = entries.map(historiaDesdeGateway);
          if (tecnica.length) {
            feed = tecnica;
            notaFinal = 'Aún no escribe su diario — esto es lo que ha estado consultando.';
          }
        } catch { /* se queda con el diario vacio */ }
      }
      setFeedIA(feed);
      setNotaFeedIA(notaFinal);
    } catch {
      try {
        const entries = await fetchActividad(grupo.sessions.map((s) => s.agentId));
        setFeedIA(entries.map(historiaDesdeGateway));
      } catch { setFeedIA([]); }
    }
  };

  const cerrarAgente = () => {
    setSelectedId(null);
    setDiario([]);
    setFeedIA([]);
    setNotaFeedIA(null);
  };

  // Misma ruta no remonta: sidebar re-click cambia location.key y/o dispara
  // fp-agentes-reset. Volvemos a la lista (personas).
  const volverAlEquipo = () => {
    setSelectedId(null);
    setDiario([]);
    setFeedIA([]);
    setNotaFeedIA(null);
    setTab('agentes');
  };

  useEffect(() => {
    volverAlEquipo();
    // location.key is enough; eslint: we want reset only when the route re-navigates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    window.addEventListener('fp-agentes-reset', volverAlEquipo);
    return () => window.removeEventListener('fp-agentes-reset', volverAlEquipo);
  }, []);

  // Cita quieta en la lista: última línea de cada bot, sin bloquear el render
  useEffect(() => {
    if (!agentes.length) { setCitas({}); return undefined; }
    let cancel = false;
    Promise.all(agentes.map((a) =>
      getAgentDiary(a.id)
        .then((d) => [a.id, (d.eventos && d.eventos[0] && d.eventos[0].motivo) || ''])
        .catch(() => [a.id, ''])
    )).then((pairs) => {
      if (!cancel) setCitas(Object.fromEntries(pairs));
    });
    return () => { cancel = true; };
  }, [agentes]);

  // Si el abierto desaparece (revocado / borrado), volvemos al equipo
  useEffect(() => {
    if (!selectedId) return;
    if (String(selectedId).startsWith('ia:')) {
      if (!loadingIA && !grupos.some((g) => g.key === selectedId)) cerrarAgente();
    } else if (!loading && !agentes.some((a) => a.id === selectedId)) {
      cerrarAgente();
    }
  }, [agentes, grupos, loading, loadingIA, selectedId]);

  const agenteAbierto = agentes.find((a) => a.id === selectedId) || null;
  const grupoAbierto = grupos.find((g) => g.key === selectedId) || null;

  // Llave detras de la IA abierta: si esta en pausa, la ficha lo dice y ofrece Reactivar.
  const [llaves, setLlaves] = useState([]);
  useEffect(() => {
    if (!selectedId) return;
    fetchLlaves().then(setLlaves).catch(() => setLlaves([]));
  }, [selectedId]);
  const llaveDelGrupo = grupoAbierto
    ? llaves.find((k) => grupoAbierto.sessions.some((ss) => ss.agentId === `key:${k.id}`)) || null
    : null;
  const enPausa = Boolean(llaveDelGrupo && llaveDelGrupo.pausada);
  const onReactivar = async () => {
    if (!llaveDelGrupo) return;
    try { await reactivarLlave(llaveDelGrupo.id); setLlaves(await fetchLlaves()); } catch { /* reintentar manual */ }
  };
  const vistaAbierta = Boolean(selectedId) && tab === 'agentes';

  const catalogoBlock = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
      {catalogo.map((c) => {
        const horizonte = c.horizonte || FICHA_TESIS[c.key]?.horizonte;
        const voz = FICHA_VOZ[c.key];
        const mood = FICHA_MOOD[c.key] || '#0a2540';
        const existente = agentes.find((a) => a.preset === c.key);
        const chips = c.universo || [];
        return (
          <div
            key={c.key}
            className="agentes-ola__card relative flex flex-col rounded-2xl border border-gray-200 p-6 pl-7"
          >
            <span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
              style={{ background: mood }}
            />
            <h3 className="text-xl font-semibold text-[#0a2540] tracking-tight">{c.nombre}</h3>
            {voz && (
              <p className="mt-1.5 text-[15px] leading-snug text-gray-600">{voz.yo}</p>
            )}

            <div className="mt-4 space-y-3">
              {c.key === 'constante' ? (
                <div>
                  <p className="text-xs text-gray-500">Con qué</p>
                  <p className="mt-0.5 text-sm text-gray-800">VOO, QQQ, SCHD, VWO, BND y GLD. Seis ETFs, no una acción.</p>
                </div>
              ) : (c.key === 'paciente' || c.key === 'cazador') ? (
                <div>
                  <p className="text-xs text-gray-500">Puede elegir</p>
                  <p className="mt-0.5 text-sm text-gray-800">
                    Mira el S&P 500 cada noche (~500 acciones). Tú ves como máximo {c.key === 'cazador' ? 3 : 4} posiciones.
                  </p>
                </div>
              ) : chips.length > 0 ? (
                <div>
                  <p className="text-xs text-gray-500">Puede elegir</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {chips.map((sym) => (
                      <span
                        key={sym}
                        className="text-xs font-medium tabular-nums px-2 py-0.5 rounded-md bg-gray-100 text-gray-800"
                      >
                        {sym}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">No los compra todos. Elige de esa lista.</p>
                </div>
              ) : null}
              {voz && (
                <div>
                  <p className="text-xs text-gray-500">No hace</p>
                  <p className="mt-0.5 text-sm text-gray-800">{voz.noHace}</p>
                </div>
              )}
              {horizonte && (
                <div>
                  <p className="text-xs text-gray-500">Horizonte</p>
                  <p className="mt-0.5 text-sm text-gray-800">{horizonte}</p>
                </div>
              )}
            </div>

            {c.comoDecide && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setComoDe(comoDe === c.key ? null : c.key)}
                  className="text-xs text-gray-500 font-medium text-left"
                >
                  {comoDe === c.key ? 'Ocultar cómo decide' : '¿Cómo decide?'}
                </button>
                {comoDe === c.key && (
                  <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-2.5 text-xs text-gray-700 space-y-1">
                    <p><span className="font-semibold">Qué mira:</span> {c.comoDecide.mira}</p>
                    <p><span className="font-semibold">Cuándo compra:</span> {c.comoDecide.compra}</p>
                    <p><span className="font-semibold">Cuándo vende:</span> {c.comoDecide.vende}</p>
                    <p className="text-gray-500 italic">Así se lee en su historia: {c.comoDecide.ejemplo}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-auto pt-5">
              {existente ? (
                <div
                  className="text-center py-2.5 rounded-xl"
                  style={{ color: mood, background: `${mood}14` }}
                >
                  <p className="text-sm font-semibold">
                    {existente.nombre ? `${existente.nombre} ya lo hace` : 'Ya está en tu equipo'}
                  </p>
                  {(existente.valorTotal != null || existente.billetera) && (
                    <p className="mt-0.5 text-xs font-medium tabular-nums opacity-80">
                      {fmtUsd(existente.valorTotal != null ? existente.valorTotal : existente.billetera?.saldo)} de práctica
                    </p>
                  )}
                </div>
              ) : creando !== c.key ? (
                <button
                  type="button"
                  onClick={() => { setCreando(c.key); setNombre(''); setErrorCard(null); }}
                  className="w-full text-sm font-semibold py-3 rounded-xl text-white"
                  style={{ background: mood }}
                >
                  Ponlo a trabajar
                </button>
              ) : null}
              {creando === c.key && (
                <div>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="Ponle un nombre (ej: Andrés)"
                      maxLength={20}
                      className="flex-1 min-w-[120px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                    />
                    <button type="button" disabled={enviando} onClick={() => onCrear(c.key)} className="text-xs px-3 py-1.5 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50">
                      {enviando ? 'Creando…' : 'Crear'}
                    </button>
                    <button type="button" onClick={() => { setCreando(null); setNombre(''); setErrorCard(null); }} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Cancelar</button>
                  </div>
                  {errorCard?.preset === c.key && errorCard.text && (
                    <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                      {errorCard.text}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="agentes-ola font-[poppins] p-4 md:p-6 max-w-3xl mx-auto">
      {!vistaAbierta && (
        <div className="agentes-ola__hero rounded-2xl p-6 text-white">
          <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-amber-400 text-amber-950">
            Práctica · {fmtUsd(fondeoInicial)} de juguete
          </span>
          <h1 className="text-2xl font-semibold mt-3">Agentes</h1>
          <p className="mt-2 text-sm opacity-90 max-w-2xl">
            En prueba empiezas con {fmtUsd(fondeoInicial)} de práctica para tus agentes. Tu IA puede mirar y ensayar. El dinero real no se mueve hasta que tú lo autorices.
          </p>
        </div>
      )}

      <div className={`${vistaAbierta ? '' : 'mt-4'} flex gap-2 border-b border-gray-200`}>
        {[
          { key: 'agentes', label: 'Mis agentes' },
          { key: 'aprende', label: 'Aprende' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => {
              if (t.key === 'agentes') cerrarAgente();
              setTab(t.key);
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-[#2A59C6] text-[#2A59C6]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'agentes' && (
      <>
      {msg && (
        <div className={`mt-4 rounded-lg px-4 py-3 text-sm ${msg.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      <PendientesAgente active={tab === 'agentes'} />

      {tab === 'agentes' && propuestas.length > 0 && (
        <div className="mt-6 space-y-3">
          {propuestas.map((pr) => (
            <div key={pr.id} className="agentes-ola__card rounded-2xl border border-[#2A59C6]/30 p-6">
              <p className="text-lg font-semibold text-[#0a2540] tracking-tight">
                {pr.nombreBot} pide tu permiso
              </p>
              <p className="mt-2 text-sm text-[#0a2540] bg-[#E6EBF7] rounded-lg px-3 py-2">
                <span className="text-gray-500">Su porqué: </span>«{pr.motivo}»
              </p>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Quiere comprar</dt>
                  <dd className="font-medium text-gray-900">{pr.symbol}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Monto</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{`$${Number(pr.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Vence</dt>
                  <dd className="text-gray-700">{new Date(pr.venceEn).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm text-gray-600">
                Es plata de práctica. Si apruebas, compra al precio del momento; si la dejas vencer, mañana re-evalúa con datos frescos.
              </p>
              {propMsg && propMsg.id === pr.id && (
                <p className={`mt-2 text-sm rounded-lg px-2.5 py-1.5 border ${propMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  {propMsg.texto}
                </p>
              )}
              <div className="mt-4 flex gap-2 flex-wrap">
                <button type="button" disabled={Boolean(propBusy)} onClick={() => onAprobarPropuesta(pr)}
                  className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50">
                  {propBusy === pr.id ? 'Un momento…' : 'Aprobar compra'}
                </button>
                <button type="button" disabled={Boolean(propBusy)} onClick={() => onDescartarPropuesta(pr)}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50">
                  Descartar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!selectedId && (
        <>
          {loading && <p className="mt-8 text-sm text-gray-500">Cargando…</p>}

          <div className="mt-8 space-y-4">
            {agentes.map((a) => {
              const frenado = a.protecciones?.frenadoPorDrawdown;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onDiario(a)}
                  className="agentes-ola__card w-full text-left rounded-2xl border border-gray-200 px-6 py-6 hover:border-[#2A59C6] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-2xl font-semibold text-[#0a2540] tracking-tight">{a.nombre}</p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 capitalize">{a.preset}</span>
                        {frenado ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Frenado por seguridad</span>
                        ) : (
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${a.estado === 'activo' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {a.estado === 'activo' ? 'Trabajando' : 'Pausado'}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xl font-semibold text-[#0a2540] tabular-nums shrink-0">{fmtUsd(a.valorTotal)}</p>
                  </div>
                  {citas[a.id] && (
                    <p className="mt-4 text-[15px] text-gray-500 italic leading-relaxed line-clamp-2">«{citas[a.id]}»</p>
                  )}
                </button>
              );
            })}

            {grupos.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => abrirIA(g)}
                className="agentes-ola__card w-full text-left rounded-2xl border border-gray-200 px-6 py-6 hover:border-[#2A59C6] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-2xl font-semibold text-[#0a2540] tracking-tight">{g.label}</p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {g.canales.map((c) => (
                        <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">{c}</span>
                      ))}
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${enPausa ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-green-50 text-green-700 border-green-200'}`}>{enPausa ? 'En pausa' : 'Activo'}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 shrink-0 pt-1">visto {fmtFecha(g.lastSeenAt)}</p>
                </div>
              </button>
            ))}
          </div>

          {!loading && agentes.length === 0 && grupos.length === 0 && (
            <p className="mt-8 text-sm text-gray-500">Todavía no tienes agentes. Pon a trabajar uno del catálogo: nace con {fmtUsd(fondeoInicial)} de práctica.</p>
          )}

          <div className="mt-10 space-y-3">
            <button
              type="button"
              onClick={() => setMostrarCodigo((v) => !v)}
              className="text-sm px-4 py-2 rounded-lg border border-[#2A59C6] text-[#2A59C6] font-medium"
            >
              {mostrarCodigo ? 'Cerrar' : 'Conectar Claude o Cursor'}
            </button>
            {mostrarCodigo && (
              <div className="pt-1 pb-2">
                <ConectaTuAgente compact />
              </div>
            )}

            <div>
              <h2 className="text-lg font-semibold text-[#0a2540]">Elige quién trabaja</h2>
              <p className="mt-1 text-sm text-gray-500">En práctica cada uno nace con $1,000 de juguete.</p>
              <div className="mt-4">
                {catalogoBlock}
              </div>
            </div>

            {proximamente.length > 0 && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setMostrarProximamente((v) => !v)}
                  className="text-xs text-gray-500 font-medium"
                >
                  Lo que viene
                </button>
                {mostrarProximamente && (
                  <div className="mt-2 space-y-2">
                    {proximamente.map((c) => (
                      <div key={c.key} className="border border-dashed border-gray-300 rounded-lg p-2.5 opacity-70">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-xs font-semibold text-gray-600">{c.nombre}</h3>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                            Próximamente
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">{c.estrategia}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            disabled
            className="mt-10 text-xs px-3 py-1.5 rounded-lg bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed"
          >
            Pasar a real · se desbloquea con práctica
          </button>
        </>
      )}

      {selectedId && agenteAbierto && (
        <div className="mt-6">
          <button type="button" onClick={cerrarAgente} className="text-sm font-medium text-[#2A59C6]">
            Volver
          </button>
          <div className="mt-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-3xl font-semibold text-[#0a2540] tracking-tight">{agenteAbierto.nombre}</h2>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 capitalize">{agenteAbierto.preset}</span>
                {agenteAbierto.protecciones?.frenadoPorDrawdown ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Frenado por seguridad</span>
                ) : (
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${agenteAbierto.estado === 'activo' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {agenteAbierto.estado === 'activo' ? 'Trabajando' : 'Pausado'}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-gray-500">Disponible</p>
              <p className="mt-1 text-lg font-semibold text-[#0a2540] tabular-nums">{fmtUsd(agenteAbierto.billetera?.saldo)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Invertido</p>
              <p className="mt-1 text-lg font-semibold text-[#0a2540] tabular-nums">{fmtUsd(invertidoDe(agenteAbierto))}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total</p>
              <p className="mt-1 text-lg font-semibold text-[#0a2540] tabular-nums">{fmtUsd(agenteAbierto.valorTotal)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">PnL</p>
              <p className={`mt-1 text-lg font-semibold tabular-nums ${Number(agenteAbierto.pnlTotal || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Number(agenteAbierto.pnlTotal || 0) >= 0 ? '+' : ''}{fmtUsd(agenteAbierto.pnlTotal)}
              </p>
            </div>
          </div>

          <div className="mt-5 flex gap-2 flex-wrap">
            <button type="button" onClick={() => onMonto(agenteAbierto, 'fondear')} className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white">Fondear</button>
            <button type="button" onClick={() => onMonto(agenteAbierto, 'retirar')} className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Retirar</button>
            <button type="button" onClick={() => onToggle(agenteAbierto)} className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
              {agenteAbierto.estado === 'activo' ? 'Pausar' : 'Reanudar'}
            </button>
          </div>

          <MandatoAgente agente={agenteAbierto} onChanged={cargar} />

          {(agenteAbierto.posiciones || []).length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400">
                    <th className="py-1.5 pr-3 font-medium">Activo</th>
                    <th className="py-1.5 pr-3 font-medium">Cant.</th>
                    <th className="py-1.5 pr-3 font-medium">Valor</th>
                    <th className="py-1.5 font-medium">PnL</th>
                  </tr>
                </thead>
                <tbody>
                  {agenteAbierto.posiciones.map((p) => (
                    <tr key={p.symbol} className="border-t border-gray-100">
                      <td className="py-2 pr-3 font-medium text-gray-800">{p.symbol}</td>
                      <td className="py-2 pr-3 text-gray-600 tabular-nums">{p.qty}</td>
                      <td className="py-2 pr-3 text-gray-800 tabular-nums">{fmtUsd(p.valor)}</td>
                      <td className={`py-2 tabular-nums ${Number(p.pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtUsd(p.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-12 space-y-8">
            {diario.length === 0 && (
              <p className="text-base text-gray-500">{HISTORIA_VACIA}</p>
            )}
            {diario.map((e) => {
              const st = EVENTO_STYLE[e.tipo] || { dot: 'bg-gray-400', label: e.tipo };
              return (
                <div key={e._id}>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    <p className="text-xs text-gray-400">{fmtFecha(e.createdAt)}</p>
                  </div>
                  {e.tipo === 'miguel' ? (
                    <p className="mt-1.5 text-[17px] leading-relaxed diario-miguel rounded-xl px-4 py-3">{e.motivo}</p>
                  ) : (
                    <p className="mt-1.5 text-[17px] leading-relaxed text-gray-800">{e.motivo}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedId && grupoAbierto && (
        <div className="mt-6">
          <button type="button" onClick={cerrarAgente} className="text-sm font-medium text-[#2A59C6]">
            Volver
          </button>
          <div className="mt-5">
            <h2 className="text-3xl font-semibold text-[#0a2540] tracking-tight">{grupoAbierto.label}</h2>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">tu IA</span>
              <span className="text-xs px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">Activo</span>
              {grupoAbierto.canales.map((c) => (
                <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{c}</span>
              ))}
            </div>
            {enPausa && (
              <div className="mt-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex-wrap">
                <p className="text-sm text-amber-800">
                  {grupoAbierto.label} se puso en pausa{llaveDelGrupo.pauseMotivo ? <>: «{llaveDelGrupo.pauseMotivo}»</> : null}. No puede usar sus herramientas hasta que lo reactives.
                </p>
                <button type="button" onClick={onReactivar} className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white whitespace-nowrap">
                  Reactivar
                </button>
              </div>
            )}
          </div>

          <div className="mt-8">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Permisos</p>
            <ul className="mt-2 space-y-1.5">
              {permisosDe(grupoAbierto.scopes).map((p) => (
                <li key={p.label} className="text-sm text-gray-700">
                  <span className={p.ok ? 'text-green-700' : 'text-gray-400'}>{p.ok ? 'Sí' : 'No'}</span>
                  <span className="text-gray-400"> · </span>
                  {p.label}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-gray-400">Mover o retirar dinero exige siempre tu confirmación. Ningún agente puede hacerlo solo.</p>
          </div>

          <div className="mt-8 space-y-2">
            {grupoAbierto.sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-700">
                  {canalDe(s)}
                  <span className="text-xs text-gray-400 ml-2">visto {fmtFecha(s.lastSeenAt)}</span>
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await revocar(s.id);
                    if (ok && grupoAbierto.sessions.length <= 1) cerrarAgente();
                    else if (ok) cargarSesiones();
                  }}
                  className="text-xs text-red-600 font-medium"
                >
                  Desconectar
                </button>
              </div>
            ))}
          </div>

          <div className="mt-12 space-y-8">
            {notaFeedIA && (
              <p className="text-xs text-gray-400">{notaFeedIA}</p>
            )}
            {feedIA.length === 0 && (
              <p className="text-base text-gray-500">{HISTORIA_VACIA}</p>
            )}
            {feedIA.map((a) => (
              <div key={a.id}>
                <p className="text-xs text-gray-400">{fmtFecha(a.createdAt || a.ts)}</p>
                <p className={`mt-1.5 text-[17px] leading-relaxed ${a.bloqueado ? 'text-red-700' : 'text-gray-800'}`}>
                  {a.texto || a.motivo || a.label || a.tool}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}

      {tab === 'aprende' && (
      <>
      {/* ¿CÓMO FUNCIONAN? — el ciclo diario en 4 pasos */}
      <div className="agentes-ola__card mt-6 border border-gray-200 rounded-xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">¿Cómo trabaja un agente?</h2>
        <p className="text-sm text-gray-500 mt-1">
          Tu agente no adivina ni promete: sigue un plan. Esto es lo que hace, todos los días, al cierre de la bolsa americana.
        </p>
        <div className="mt-4 grid md:grid-cols-4 gap-3">
          {[
            { n: '1', t: 'Mira el mercado', d: 'Recibe las señales del día: precios reales, si hay tendencia, qué tan agitado está cada activo, si algo cayó exagerado.' },
            { n: '2', t: 'Aplica sus reglas', d: 'Cada agente tiene reglas fijas escritas de antemano. Sin emociones y sin improvisar: si se cumplen, actúa; si no, se queda quieto.' },
            { n: '3', t: 'Pasan los frenos', d: 'Antes de cualquier compra revisan: ¿pérdida reciente? ¿mercado muy revuelto? ¿la billetera cayó demasiado? Un freno le gana a cualquier regla.' },
            { n: '4', t: 'Te lo cuenta', d: 'Todo queda en su diario con el porqué — también lo que decidió NO hacer.' },
          ].map((p) => (
            <div key={p.n} className="rounded-lg bg-gray-50 p-4">
              <div className="w-7 h-7 rounded-full bg-[#2A59C6] text-white text-sm font-semibold flex items-center justify-center">{p.n}</div>
              <p className="mt-2 text-sm font-semibold text-gray-900">{p.t}</p>
              <p className="mt-1 text-xs text-gray-600">{p.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-gray-500">
          <span className="font-medium text-gray-700">¿De dónde sale la información?</span> De los precios reales del mercado
          americano y del análisis propio de FlowexPay, actualizados cada día al cierre. Tu agente no usa datos de otras personas ni promete rendimientos.
        </p>
      </div>

      {/* Lo que un agente NUNCA puede hacer — la confianza es el producto */}
      <div className="agentes-ola__card mt-8 border border-gray-200 rounded-xl p-5">
        <h3 className="text-base font-semibold text-gray-900">Lo que tu agente NUNCA puede hacer</h3>
        <ul className="mt-2 text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>Retirar plata: solo tú sacas dinero de su billetera.</li>
          <li>Tocar tu saldo principal: solo maneja lo que tú le diste.</li>
          <li>Pasarse de sus límites: los frenos de seguridad no son opcionales.</li>
          <li>Operar a escondidas: todo queda en su diario, con el porqué.</li>
        </ul>
        <p className="mt-3 text-xs text-gray-400">
          Modo práctica: la plata de los agentes es 100% ficticia y las acciones son de simulación,
          con precios reales del mercado. Cuando el modo real esté disponible, activarlo será un
          paso explícito con tu código SMS.
        </p>
      </div>

      {/* LA BIBLIOTECA DEL AGENTE — aprende antes de (o mientras) usas */}
      {biblioteca.length > 0 && (
        <div className="mt-8" id="biblioteca-agente">
          <h2 className="text-lg font-semibold text-gray-900">Aprende: la Biblioteca del Agente</h2>
          <p className="text-sm text-gray-500 mt-1">
            Todo lo que deberías saber, sin letra chica. El Oráculo responde con esta misma información
            si prefieres preguntarle conversando.
          </p>
          <div className="mt-4 space-y-2">
            {biblioteca.map((d) => (
              <div key={d.slug} className="agentes-ola__card border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setDocAbierto(docAbierto === d.slug ? null : d.slug)}
                  className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3"
                >
                  <span>
                    <span className="text-sm font-semibold text-gray-900 block">{d.titulo}</span>
                    <span className="text-xs text-gray-500">{d.resumen}</span>
                  </span>
                  <span className={`text-gray-400 transition-transform ${docAbierto === d.slug ? 'rotate-180' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>
                  </span>
                </button>
                {docAbierto === d.slug && (
                  <div className="px-5 pb-4 space-y-2 border-t border-gray-100 pt-3">
                    {d.cuerpo.map((p, i) => (
                      <p key={i} className="text-sm text-gray-600">{p}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
